import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

const PlansPage = () => {
  const [selectedDuration, setSelectedDuration] = useState('all');
  
  const plans = [
    {
      id: 1,
      name: "Tourist Starter",
      price: "10,000",
      duration: "3 days",
      data: "2GB",
      features: ["High-speed 4G/5G", "Instant activation", "No roaming charges", "Basic support"],
      popular: false,
      category: "short"
    },
    {
      id: 2,
      name: "Tourist",
      price: "15,000",
      duration: "7 days",
      data: "5GB",
      features: ["High-speed 4G/5G", "Instant activation", "No roaming charges", "24/7 support"],
      popular: false,
      category: "short"
    },
    {
      id: 3,
      name: "Tourist Plus",
      price: "25,000",
      duration: "14 days",
      data: "10GB",
      features: ["Premium 5G network", "Hotspot sharing", "Priority support", "Free local calls"],
      popular: false,
      category: "medium"
    },
    {
      id: 4,
      name: "Business",
      price: "35,000",
      duration: "30 days", 
      data: "20GB",
      features: ["Premium 5G network", "Hotspot sharing", "Priority support", "Unlimited local calls"],
      popular: true,
      category: "medium"
    },
    {
      id: 5,
      name: "Business Pro",
      price: "50,000",
      duration: "30 days",
      data: "35GB",
      features: ["Maximum coverage", "International roaming", "VIP support", "Data rollover", "Business priority"],
      popular: false,
      category: "medium"
    },
    {
      id: 6,
      name: "Extended",
      price: "65,000",
      duration: "90 days",
      data: "50GB",
      features: ["Maximum coverage", "International roaming", "VIP support", "Data rollover"],
      popular: false,
      category: "long"
    },
    {
      id: 7,
      name: "Extended Pro",
      price: "95,000",
      duration: "90 days",
      data: "100GB",
      features: ["Maximum coverage", "International roaming", "VIP support", "Data rollover", "Premium speeds"],
      popular: false,
      category: "long"
    },
    {
      id: 8,
      name: "Annual",
      price: "180,000",
      duration: "365 days",
      data: "200GB",
      features: ["Best value", "Maximum coverage", "International roaming", "VIP support", "Data rollover", "Premium perks"],
      popular: false,
      category: "long"
    }
  ];

  const filteredPlans = selectedDuration === 'all' 
    ? plans 
    : plans.filter(plan => plan.category === selectedDuration);

  const handlePurchase = (planId) => {
    // TODO: Implement purchase flow
    console.log('Purchasing plan:', planId);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Myanmar eSIM Plans
            </h1>
            <p className="text-lg text-blue-100 mb-8">
              Stay connected with high-speed data plans. Instant activation, no physical SIM needed.
            </p>
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
              <span className="text-white text-sm font-medium">Coverage across Myanmar</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <div className="inline-flex bg-gray-100 rounded-xl p-1">
              {[
                { key: 'all', label: 'All Plans' },
                { key: 'short', label: 'Short Term' },
                { key: 'medium', label: 'Medium Term' },
                { key: 'long', label: 'Long Term' }
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setSelectedDuration(filter.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    selectedDuration === filter.key
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {filteredPlans.map((plan) => (
              <div key={plan.id} className={`relative bg-white rounded-2xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 ${plan.popular ? 'ring-2 ring-blue-500 shadow-lg' : ''}`}>
                {plan.popular && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Popular
                    </span>
                  </div>
                )}
                
                <div className="p-5">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{plan.name}</h3>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {plan.price} <span className="text-sm text-gray-600 font-normal">MMK</span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-4 mb-4 text-center">
                    <div className="text-xl font-bold text-blue-600 mb-1">{plan.data}</div>
                    <div className="text-sm text-gray-600">{plan.duration}</div>
                  </div>
                  
                  <ul className="space-y-2 mb-5">
                    {plan.features.slice(0, 3).map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-xs text-gray-600">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => handlePurchase(plan.id)}
                    className={`w-full py-3 px-4 rounded-xl font-medium transition-all duration-300 text-sm ${
                      plan.popular 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    Get Started
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "How does eSIM work?",
                answer: "eSIM is a digital SIM that allows you to activate a cellular plan without using a physical SIM card. Simply scan a QR code to download and activate your plan."
              },
              {
                question: "Is my device compatible?",
                answer: "Most modern smartphones support eSIM including iPhone XS and later, Google Pixel 3 and later, Samsung Galaxy S20 and later. Check your device settings for eSIM capability."
              },
              {
                question: "Can I use eSIM alongside my physical SIM?",
                answer: "Yes! Most eSIM-compatible devices support dual SIM functionality, allowing you to use both your regular SIM and eSIM simultaneously."
              },
              {
                question: "How long does activation take?",
                answer: "eSIM activation is instant. Once you complete your purchase, you'll receive a QR code via email that you can scan to activate your plan immediately."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Need Help Choosing?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our customer support team is available 24/7 to help you find the perfect plan for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="bg-white hover:bg-gray-100 text-blue-600 font-bold py-3 px-8 rounded-lg transition-colors duration-300"
            >
              Contact Support
            </Link>
            <Link 
              to="/auth"
              className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-300"
            >
              Get Started
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlansPage;